import React from 'react';
import { toast } from 'react-toastify';
import './toast.css';
import toastSuccess from 'assets/icons/toast-success.svg';
import toastLoading from 'assets/icons/toast-loading.svg';
import toastError from 'assets/icons/toast-error.svg';

/**
 * Toast card design — ported from the doc-gen v2 toast card (Figma
 * "Components" frame, node 1409-2490), recolored to the FundMore palette
 * (design-tokens.css): white body, 10px state-colored left accent bar,
 * 24px state icon, Raleway-bold title + Open Sans description, bordered
 * Dismiss button. Variants: success, loading, error, info.
 */

const DEFAULT_TITLES = {
  success: 'Success',
  error: 'Something went wrong',
  loading: 'Loading…',
  info: 'Info',
};

// Accepts the legacy single string (or element) message as the description,
// or { title, description } for call sites that want both lines.
const normalizeContent = (input, variant) => {
  if (
    input &&
    typeof input === 'object' &&
    !React.isValidElement(input) &&
    ('title' in input || 'description' in input)
  ) {
    return {
      title: input.title || DEFAULT_TITLES[variant],
      description: input.description,
    };
  }
  return { title: DEFAULT_TITLES[variant], description: input };
};

const ToastIcon = ({ variant }) => {
  switch (variant) {
    case 'success':
      return (
        <span className="app-toast__icon" aria-hidden="true">
          <img src={toastSuccess} alt="" />
        </span>
      );
    case 'loading':
      return (
        <span className="app-toast__icon app-toast__icon--spin" aria-hidden="true">
          <img src={toastLoading} alt="" />
        </span>
      );
    case 'error':
      return (
        <span className="app-toast__icon" aria-hidden="true">
          <img src={toastError} alt="" />
          <span className="app-toast__icon-mark">!</span>
        </span>
      );
    case 'info':
      return (
        <span className="app-toast__icon" aria-hidden="true">
          <span className="app-toast__icon-circle">i</span>
        </span>
      );
    default:
      return null;
  }
};

/**
 * Reusable toast body rendered inside react-toastify.
 *
 * @param {'success'|'error'|'loading'|'info'} variant - Drives accent bar color and icon.
 * @param {string} title - Bold headline line.
 * @param {string|ReactNode} [description] - Secondary detail line.
 * @param {{label: string, onClick: function}} [action] - Optional action button (e.g. View).
 * @param {function} onDismiss - Closes the toast (react-toastify closeToast).
 */
export const ToastContent = ({ variant, title, description, action, onDismiss }) => (
  <div className={`app-toast app-toast--${variant}`}>
    <span className="app-toast__bar" aria-hidden="true" />
    <ToastIcon variant={variant} />
    <div className="app-toast__text">
      <p className="app-toast__title">{title}</p>
      {description ? <p className="app-toast__description">{description}</p> : null}
    </div>
    {action && typeof action.onClick === 'function' && (
      <button
        type="button"
        className="app-toast__action"
        onClick={() => {
          action.onClick();
          onDismiss?.();
        }}
      >
        {action.label}
      </button>
    )}
    <button type="button" className="app-toast__dismiss" onClick={onDismiss}>
      Dismiss
    </button>
  </div>
);

// Kept for the container-level closeButton in app.jsx; only reached by
// toasts fired outside customToast. Matches the card Dismiss button.
export const CloseButton = ({ closeToast }) => (
  <button type="button" className="app-toast__dismiss" onClick={closeToast}>
    Dismiss
  </button>
);

const renderToast = (variant, { title, description, action }) =>
  function AppToast({ closeToast }) {
    return (
      <ToastContent
        variant={variant}
        title={title}
        description={description}
        action={action}
        onDismiss={closeToast}
      />
    );
  };

const CARD_OPTIONS = {
  className: 'app-toast__card',
  icon: false,
  closeButton: false,
  closeOnClick: false,
};

const showToast = (variant, input, options = {}) =>
  toast(renderToast(variant, normalizeContent(input, variant)), {
    ...CARD_OPTIONS,
    ...options,
  });

export const customToast = {
  success: (input, options) => showToast('success', input, options),
  error: (input, options) => showToast('error', input, options),
  info: (input, options) => showToast('info', input, options),
  // Returns the toast id so callers can toast.dismiss(id) when done.
  loading: (input, options) => showToast('loading', input, { autoClose: false, ...options }),
  documentSigned: ({ fileName, applicationRef } = {}) =>
    showToast(
      'success',
      {
        title: 'Document signed',
        description: `“${fileName || 'Document'}” was signed via DocuSign${
          applicationRef ? ` · Application ${applicationRef}` : ''
        }`,
      },
      { autoClose: 10000 },
    ),
  documentGenerated: ({ fileName, onView } = {}) =>
    toast(
      renderToast('success', {
        title: 'Document generated',
        description: `“${fileName || 'Document'}” was generated`,
        action: typeof onView === 'function' ? { label: 'View', onClick: onView } : undefined,
      }),
      { ...CARD_OPTIONS, autoClose: 10000 },
    ),
  // Batched variant: a bulk generation lands many docs at once, so collapse
  // them into a single grouped toast instead of one-per-document (which
  // previously flooded the screen with a stack of identical cards).
  documentsGenerated: ({ count, names = [], onViewAll } = {}) => {
    const total = count ?? names.length;
    const preview = names.slice(0, 2).map((n) => `“${n}”`).join(', ');
    const remaining = total - Math.min(2, names.length);
    const description = preview
      ? `${preview}${remaining > 0 ? ` and ${remaining} more` : ''} were generated`
      : `${total} documents were generated`;
    return toast(
      renderToast('success', {
        title: `${total} documents generated`,
        description,
        action:
          typeof onViewAll === 'function' ? { label: 'View all', onClick: onViewAll } : undefined,
      }),
      { ...CARD_OPTIONS, autoClose: 12000 },
    );
  },
  promise: (func, pendingMsg, successMsg, errorMessage) =>
    toast.promise(func, {
      pending: {
        render: renderToast('loading', normalizeContent(pendingMsg, 'loading')),
        ...CARD_OPTIONS,
      },
      success: {
        render: renderToast('success', normalizeContent(successMsg, 'success')),
        ...CARD_OPTIONS,
      },
      error: {
        render: renderToast('error', normalizeContent(errorMessage, 'error')),
        ...CARD_OPTIONS,
      },
    }),
};
